import React from 'react';
import { Skeleton, Box, Divider } from '@mui/material';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';

// Skeleton for individual version comparison card
export const VersionComparisonSkeleton = () => (
  <Box
    sx={{
      width: '500px',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    }}
  >
    {/* Version name skeleton */}
    <Skeleton variant="text" width={200} height={40} />

    {/* Details section skeleton */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} variant="text" width={300 - i * 75} height={20} />
      ))}
    </Box>

    {/* Metrics cards skeleton */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" width="100%" height={80} sx={{ borderRadius: '8px' }} />
      ))}
    </Box>
  </Box>
);

// Skeleton for content card - matches ContentCard component width
export const ContentCardSkeleton = () => (
  <Box
    sx={{
      width: '50%',
      borderRadius: '8px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    }}
  >
    {/* Title */}
    <Skeleton variant="text" width={100} height={24} />

    {/* Content lines */}
    {Array.from({ length: 3 }).map((_, i) => (
      <Skeleton key={i} variant="text" width={`${100 - i * 5}%`} height={20} />
    ))}
  </Box>
);

// Skeleton for description card (taller) - matches ContentCard component width
export const DescriptionCardSkeleton = () => (
  <Box
    sx={{
      width: '50%',
      borderRadius: '8px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      minHeight: '752px'
    }}
  >
    {/* Title */}
    <Skeleton variant="text" width={120} height={24} />

    {/* Content lines */}
    {Array.from({ length: 15 }).map((_, i) => (
      <Skeleton key={i} variant="text" width={`${100 - (i % 3) * 5}%`} height={20} />
    ))}
  </Box>
);

// Skeleton for one side of the comparison (left or right)
const SingleSideComparisonSkeleton = () => (
  <>
    {/* Title skeleton */}
    <ContentCardSkeleton />
    <Divider sx={{ margin: '32px 0px' }} />

    {/* Bullets skeleton - show 5 bullets */}
    {Array.from({ length: 5 }).map((_, index) => (
      <React.Fragment key={`bullet-skeleton-${index}`}>
        <ContentCardSkeleton />
        <Divider sx={{ margin: '32px 0px' }} />
      </React.Fragment>
    ))}

    {/* Description skeleton */}
    <DescriptionCardSkeleton />
  </>
);

// Skeleton for the entire comparison view
export const ContentComparisonSkeleton = () => (
  <>
    {/* Title comparison skeleton */}
    <Flex gap={16}>
      <ContentCardSkeleton />
      <ContentCardSkeleton />
    </Flex>
    <Divider sx={{ margin: '32px 0px' }} />

    {/* Bullets comparison skeleton - show 5 bullet pairs */}
    {Array.from({ length: 5 }).map((_, index) => (
      <React.Fragment key={`bullet-skeleton-${index}`}>
        <Flex gap={16}>
          <ContentCardSkeleton />
          <ContentCardSkeleton />
        </Flex>
        <Divider sx={{ margin: '32px 0px' }} />
      </React.Fragment>
    ))}

    {/* Description comparison skeleton */}
    <Flex gap={16}>
      <DescriptionCardSkeleton />
      <Divider sx={{ margin: '32px 0px' }} />
      <DescriptionCardSkeleton />
    </Flex>

    {/* Action bar skeleton */}
    <Flex
      justifyContent="space-between"
      sx={{
        marginTop: '64px'
      }}
    >
      <Flex gap={16}>
        <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: '4px' }} />
      </Flex>
      <Flex gap={16}>
        <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: '4px' }} />
        <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: '4px' }} />
      </Flex>
    </Flex>
  </>
);

// Skeleton for partial comparison view (when one side is loading)
export const PartialComparisonSkeleton = ({
  leftLoading,
  rightLoading
}: {
  leftLoading: boolean;
  rightLoading: boolean;
}) => (
  <>
    {/* Title comparison */}
    <Flex gap={16}>
      {leftLoading ? <ContentCardSkeleton /> : null}
      {rightLoading ? <ContentCardSkeleton /> : null}
    </Flex>
    {(leftLoading || rightLoading) && <Divider sx={{ margin: '32px 0px' }} />}

    {/* Bullets comparison - show 5 bullet pairs */}
    {Array.from({ length: 5 }).map((_, index) => (
      <React.Fragment key={`bullet-skeleton-${index}`}>
        <Flex gap={16}>
          {leftLoading ? <ContentCardSkeleton /> : null}
          {rightLoading ? <ContentCardSkeleton /> : null}
        </Flex>
        {(leftLoading || rightLoading) && <Divider sx={{ margin: '32px 0px' }} />}
      </React.Fragment>
    ))}

    {/* Description comparison */}
    <Flex gap={16}>
      {leftLoading ? <DescriptionCardSkeleton /> : null}
      {(leftLoading || rightLoading) && <Divider sx={{ margin: '32px 0px' }} />}
      {rightLoading ? <DescriptionCardSkeleton /> : null}
    </Flex>
  </>
);
